// src/utils/dashboardPlan.js
//
// The dashboard's two reasons to come back, worked out from what is already
// stored. Neither is a game: nothing is counted up, nothing is lost by staying
// away, and nobody is told they are behind.
//
//   - findResumePoint: "pick up where you left off" - the module you were in
//     the middle of (and the part), or else the next one you haven't finished.
//   - pickPracticeForWeek: "one thing to try this week" - one of the "This
//     week" practices from the summary of a module you have finished, the
//     same one all week. These are the modules' own suggestions; nothing here
//     is written for the dashboard.
//
// Pure functions (no React, no storage of their own) so they can be tested.
// The callers pass in the readers for part progress and reading position
// (utils/modulePartProgress.js).
//
// Australian English throughout.

const partsOf = (module) =>
  (Array.isArray(module?.microSessions) ? module.microSessions : []).map((session, index) => ({
    id: session?.id ?? `part-${index + 1}`,
    number: index + 1,
    title: session?.title || `Part ${index + 1}`,
  }));

/**
 * Where to pick up.
 *
 * Returns one of:
 *   { kind: 'continue', module, part, partsDone, partsTotal }
 *       a module you have started and not finished; `part` is where you
 *       stopped (or the first part not yet marked done);
 *   { kind: 'start', module }
 *       nothing in progress: the first module you haven't finished;
 *   { kind: 'done' }
 *       every module finished.
 *
 * "In progress" means a module with a saved reading position or any part
 * marked done, that isn't finished. If there are several, the one you were in
 * most recently wins.
 */
export function findResumePoint({
  modules = [],
  completedModules = [],
  readParts = () => [],
  readPosition = () => null,
} = {}) {
  const finished = new Set((completedModules || []).map(Number));
  const ordered = [...modules].filter(Boolean).sort((a, b) => a.id - b.id);
  const unfinished = ordered.filter((m) => !finished.has(Number(m.id)));
  if (unfinished.length === 0) return { kind: 'done' };

  const inProgress = [];
  for (const module of unfinished) {
    const parts = partsOf(module);
    const done = new Set((readParts(module.id) || []).map(String));
    const position = readPosition(module.id);
    if (done.size === 0 && !position) continue;

    // Where you were, if that was a part; otherwise the first part you
    // haven't marked done; otherwise (all parts done, summary not left yet)
    // the last part.
    const atPart = position ? parts.find((p) => String(p.id) === String(position.viewId)) : null;
    const firstOpen = parts.find((p) => !done.has(String(p.id)));
    const part = atPart || firstOpen || parts[parts.length - 1] || null;

    inProgress.push({
      module,
      part,
      partsDone: parts.filter((p) => done.has(String(p.id))).length,
      partsTotal: parts.length,
      at: Number(position?.at) || 0,
    });
  }

  if (inProgress.length > 0) {
    inProgress.sort((a, b) => (b.at - a.at) || (a.module.id - b.module.id));
    const { module, part, partsDone, partsTotal } = inProgress[0];
    return { kind: 'continue', module, part, partsDone, partsTotal };
  }

  return { kind: 'start', module: unfinished[0] };
}

/** Weeks since a fixed Monday, so the pick changes once a week, on Mondays. */
export const weekNumber = (now = new Date()) => {
  const MONDAY = Date.UTC(2024, 0, 1); // 1 January 2024 was a Monday
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.floor((today - MONDAY) / (7 * 24 * 60 * 60 * 1000));
};

/**
 * One practice to try this week, from the module you finished most recently
 * (by the date recorded when you finished it; failing that, the highest-
 * numbered finished module). The same practice all week; next week, the next
 * one in that module's list.
 *
 * Returns { practice: { title, description, duration }, module } or null when
 * you haven't finished a module yet (the practices assume you've read it).
 */
export function pickPracticeForWeek({
  modules = [],
  completedModules = [],
  moduleCompletedAt = {},
  now = new Date(),
} = {}) {
  const finished = new Set((completedModules || []).map(Number));
  const candidates = modules
    .filter((m) => m && finished.has(Number(m.id)))
    .map((m) => ({
      module: m,
      practices: (m.summary?.nextSteps?.practices || []).filter((p) => p && p.title && p.description),
      at: Date.parse(moduleCompletedAt?.[m.id] || '') || 0,
    }))
    .filter((c) => c.practices.length > 0);
  if (candidates.length === 0) return null;

  candidates.sort((a, b) => (b.at - a.at) || (b.module.id - a.module.id));
  const { module, practices } = candidates[0];
  const index = ((weekNumber(now) % practices.length) + practices.length) % practices.length;
  return { practice: practices[index], module };
}

export default { findResumePoint, pickPracticeForWeek, weekNumber };
